import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { useManageSubscriptionDrawerContext } from './context/manage-subscription-drawer-context';
import { isFeatureIncludedInBillingPlan } from './hooks/use-plan-details';
import { messages } from './messages';
import style from './style.module.less';
import userDeactivationModalStyles from './user-deactivation-modal.module.less';
import { UserDeactivationTable } from './user-deactivation-table';
import ModalPortal from '../../../components/modal-portal';
import { PurchaseAdditionalUsers } from '../../../constants/features';
import Global from '../../../store/global-selectors';

const MODAL_HEADER_ID = 'user-deactivation-modal-header';
/**
 * User Deactivation Modal component
 *
 * Displays a modal that allows administrators to view and deactivate users when downgrading to a plan
 * with a lower user limit. The modal shows the current number of active users, the maximum allowed users
 * for the selected plan, and provides visual indicators when the number of active users exceeds the limit.
 * @param {object} props - Component props
 * @param {()=>void} props.onClose - Callback function to close the modal
 * @param {()=>void} props.onSave - Callback function to save the modal
 * @param {()=>void} [props.onSkip] - Optional callback function to skip user deactivation and proceed with plan change
 * @returns {React.ReactElement} The UserDeactivationModal component
 */
export const UserDeactivationModal = ({ onClose, onSave, onSkip }) => {
  const intl = useIntl();
  const users = useSelector(Global.selectSellerActiveUsers);
  const { selectedPlan, showAnnualPlans } = useManageSubscriptionDrawerContext();

  const { userLimit: { limit: desiredPlanUserLimit = 0 } = {} } = selectedPlan;
  const isPurchaseAdditionalUsersFeatureIncluded = isFeatureIncludedInBillingPlan(selectedPlan, PurchaseAdditionalUsers);

  const [localUsers, setLocalUsers] = useAsyncSafeState(users);

  const maxAllowedUsers = desiredPlanUserLimit;
  const numberOfActiveUsers = localUsers.filter((user) => user.isActive).length;

  const isPurchaseAdditionalUsersFeatureGated = !isPurchaseAdditionalUsersFeatureIncluded;
  const shouldShowSkipButton = onSkip && showAnnualPlans;

  return (
    <ModalPortal onClose={onClose}>
      <Modal className={style['modal-base']} modalHeaderId={MODAL_HEADER_ID} size='medium'>
        <ModalHeader closeModal={onClose} id={MODAL_HEADER_ID}>
          <Text textType={TextType.H3} value={messages.deactivateUsersHeader} />
        </ModalHeader>
        <ModalBody className={userDeactivationModalStyles['modal-body']}>
          {isPurchaseAdditionalUsersFeatureGated ? (
            <BannerMessageV2
              content={<Text textType={TextType.Paragraph} value={messages.downgradeWarning} />}
              layout={BannerLayout.Default}
              status={BannerStatus.Error}
            />
          ) : null}
          <Text textType={TextType.Paragraph} value={messages.userLimitDescription} />
          <UserDeactivationTable
            deactivateTheme={THEME_CONSTANTS.secondary}
            maxAllowedUsers={maxAllowedUsers}
            setUsers={setLocalUsers}
            users={localUsers}
          />
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} theme={THEME_CONSTANTS.tertiary}>
            <Text textType={TextType.Paragraph} value={messages.cancel} />
          </Button>
          {shouldShowSkipButton ? (
            <Tooltip position='top' text={intl.formatMessage(messages.skipForNowTooltip)}>
              <Button className={userDeactivationModalStyles['skip-button']} onClick={onSkip} theme={THEME_CONSTANTS.tertiary}>
                <Text textType={TextType.Paragraph} value={messages.skipForNow} />
              </Button>
            </Tooltip>
          ) : null}
          <Button disabled={numberOfActiveUsers > maxAllowedUsers} onClick={onSave} theme={THEME_CONSTANTS.primary}>
            <Text textType={TextType.Paragraph} value={messages.downgradeButtonText} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
